import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react';

import { Link } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { ErrorLabel, Picture, ProductPrice } from '../../../components';
import {
    DiscountLabel,
    IconButton,
    NumberInputWithControls,
    WishlistButton,
} from '../../../compositions';
import { FormProps } from '../../../entities/@forms/Form/Form';
import { CartItem as CartItemInterface } from '../../../entities/@shop/Cart/Cart';
import { ImageFormatSize } from '../../../entities/Media/Media';
import { AppRoute, appRoutes } from '../../../entities/Routing/Routing';
import { replaceUrlParamKeysWithValues } from '../../../helpers/url';
import { useTrans } from '../../../hooks';
import {
    ProductQuantityErrors,
    ProductQuantitySubmitFormData,
    ProductQuantityValidationData,
    validateProductQuantity,
} from './validations';

import './CartItemForm.scss';

interface CartItemFormProps extends CartItemInterface, Omit<FormProps<ProductQuantitySubmitFormData>, 'onSubmit'> {
    showWishlistButton?: boolean;
    showBigRemoveButton?: boolean;
    isEditMode?: boolean;
    isLoading?: boolean;
    wishlistItems?: string[];
    onClickToProductPage?: () => void;
    onRemoveItemFromCart?: (cartItemId: string) => void;
    onUpdateCartItem?: (data: ProductQuantitySubmitFormData) => void;
    onUpdateCartItemIsInWishlist?: (productId: string, isInWishlist: boolean) => void;
    className?: string;
}

export const CartItemForm: FC<CartItemFormProps> = ({
    showWishlistButton,
    showBigRemoveButton,
    isEditMode,
    isLoading,
    wishlistItems = [],
    stock,
    id,
    variantId,
    image,
    name,
    relativeUrl,
    quantity,
    price,
    onClickToProductPage,
    onRemoveItemFromCart,
    onUpdateCartItem,
    onUpdateCartItemIsInWishlist,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const [newQuantity, setNewQuantity] = useState<number>(quantity);
    const [isInWishlist, setIsInWishlist] = useState<boolean>(false);

    const [formErrors, setFormErrors] = useState<ProductQuantityErrors>({});

    useEffect((): void => {
        if (wishlistItems.length === 0 || !variantId) {
            return;
        }

        const isProductVariantInWishlist = wishlistItems.includes(variantId);

        setIsInWishlist(isProductVariantInWishlist);
    }, [wishlistItems]);

    const handleRemoveFromCart = (): void => {
        if (!onRemoveItemFromCart) {
            return;
        }

        onRemoveItemFromCart(id);
    };

    const handleUpdateWishlistStatus = (): void => {
        if (!onUpdateCartItemIsInWishlist || !variantId) {
            return;
        }

        setIsInWishlist(!isInWishlist);

        onUpdateCartItemIsInWishlist(variantId, isInWishlist);
    };

    useDebounce((): void => {
        setFormErrors({});

        const hasChangedQuantity = newQuantity && quantity !== newQuantity;

        if (!hasChangedQuantity) {
            return;
        }

        const formData: ProductQuantityValidationData = {
            quantity: newQuantity,
            stock,
        };

        const [errors, hasErrors] = validateProductQuantity(formData);

        setFormErrors(errors);

        if (hasErrors) {
            return;
        }

        const updatedCartItemData: ProductQuantitySubmitFormData = {
            cartItemId: id,
            quantity: newQuantity,
        };

        if (onUpdateCartItem) {
            onUpdateCartItem(updatedCartItemData);
        }
    }, 450, [newQuantity]);

    const productPath = trans(appRoutes[AppRoute.productDetail].path);
    const productUrl = replaceUrlParamKeysWithValues(productPath, {
        slug: relativeUrl,
    });

    return (
        <div className={`cart-item-form ${className}`}>
            {isEditMode && showBigRemoveButton && (
                <IconButton
                    hideLabel
                    text={trans('common.delete')}
                    icon="cross"
                    disabled={isLoading}
                    onClick={handleRemoveFromCart}
                    className="cart-item-form__button cart-item-form__button--close"
                />
            )}

            <div className="cart-item-form__info-wrapper">
                <DiscountLabel
                    price={price}
                    className="cart-item-form__discount-label"
                />

                {image && (
                    <div className="cart-item-form__image">
                        <Picture
                            {...image}
                            formatOverride={ImageFormatSize.thumbnail}
                            className="cart-item-form__picture"
                        />
                    </div>
                )}

                <div className="cart-item-form__content-wrapper">
                    <Link
                        to={productUrl}
                        onClick={onClickToProductPage}
                        className="cart-item-form__name"
                    >
                        {name}
                    </Link>

                    <div className="cart-item-form__price-wrapper">
                        {!isEditMode && (
                            <p className="cart-item-form__quantity">
                                {quantity}
                                x
                            </p>
                        )}

                        <ProductPrice
                            {...price}
                            isLoading={isLoading}
                            className="cart-item-form__price"
                        />
                    </div>

                    <div className="cart-item-form__controls-wrapper">
                        {isEditMode && (
                            <form>
                                {/* TODO: When product quantity has updated, the newQuantity is not updated. */}
                                {/* This is an issue in the NumberInputWithControls (<Input />) */}
                                <NumberInputWithControls
                                    hideLabel
                                    label={trans('containers.cartItem.changeQuantity')}
                                    value={newQuantity}
                                    min={1}
                                    disabled={isLoading}
                                    onChange={setNewQuantity}
                                    className="cart-item-form__quantity-input"
                                    inputFieldClassName="cart-item-form__quantity-input-field"
                                    buttonClassName="cart-item-form__quantity-button"
                                />
                            </form>
                        )}

                        <div className="cart-item-form__control-button-wrapper">
                            {showWishlistButton && (
                                <WishlistButton
                                    isActive={isInWishlist}
                                    text={isInWishlist ? trans('common.removeFromWishlist') : trans('common.addToWishlist')}
                                    disabled={isLoading || !variantId}
                                    onClick={handleUpdateWishlistStatus}
                                    className="cart-item-form__icon-button"
                                />
                            )}

                            {isEditMode && !showBigRemoveButton && (
                                <IconButton
                                    hideLabel
                                    text={trans('common.delete')}
                                    icon="cross"
                                    disabled={isLoading}
                                    onClick={handleRemoveFromCart}
                                    className="cart-item-form__icon-button"
                                />
                            )}
                        </div>
                    </div>

                    {formErrors.quantity && (
                        <ErrorLabel
                            text={formErrors.quantity}
                            className="cart-item-form__error"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
